import React from "react";
import { Link } from "react-router-dom";
import siteConfig from "../config/siteConfig";
import "../css/privacy.scss";

function Privacy() {
  const contact = siteConfig.contact || {};
  const lastUpdated = siteConfig.policyLastUpdated || "16 October 2026";

  return (
    <main className="content-page legal-page privacy-page">
      <header className="page-hero page-hero--compact">
        <div className="page-shell page-hero__content">
          <p className="eyebrow">Your information</p>
          <h1>Privacy policy</h1>
          <p className="page-hero__lede">Last updated: {lastUpdated}</p>
          <p className="page-hero__lede">
            How {siteConfig.brandName} collects, uses, stores, discloses, and
            protects personal information—and how to make a request or complaint.
          </p>
        </div>
      </header>

      <section className="page-section page-shell legal-layout">
        <nav className="policy-nav" aria-label="Privacy policy sections">
          <p className="eyebrow">Contents</p>
          <a href="#privacy-scope">Scope &amp; current status</a>
          <a href="#privacy-collection">What we collect</a>
          <a href="#privacy-use">How we use information</a>
          <a href="#privacy-sharing">Disclosure &amp; overseas access</a>
          <a href="#privacy-rights">Access, correction &amp; choices</a>
          <a href="#privacy-complaints">Complaints</a>
          <a href="#privacy-contact">Contact us</a>
        </nav>

        <article className="prose legal-copy">
          <section id="privacy-scope">
            <h2>1. Scope and current storefront status</h2>
            <p>
              This policy applies to personal information handled through the
              {` ${siteConfig.brandName} `}website, customer care, orders, and
              related business inquiries. Where the Australian Privacy Act 1988
              and Australian Privacy Principles apply, we handle information in
              accordance with those requirements. We also honour other privacy
              rights that apply to a visitor or transaction.
            </p>
            <p>
              This version of the site is a front-end demonstration. Cart and
              wishlist choices stay in your browser. Account, contact, wholesale,
              and gifting forms validate locally and do not transmit their
              contents. Checkout creates a short-lived order summary in the
              browser and hands limited order details to the included demonstration
              payment endpoint; it does not collect card numbers on this page.
            </p>
          </section>

          <section id="privacy-collection">
            <h2>2. Personal information we collect</h2>
            <p>A connected production store may collect:</p>
            <ul>
              <li><strong>Identity and contact details,</strong> such as name, email, phone number, billing and delivery addresses, and account identifiers.</li>
              <li><strong>Order and transaction details,</strong> such as products, quantities, gift messages, delivery instructions, discounts, refunds, and limited payment status received from a payment provider.</li>
              <li><strong>Customer care and business communications,</strong> including return evidence, reviews, survey responses, wholesale or gifting inquiries, and marketing choices.</li>
              <li><strong>Device and activity information,</strong> such as IP address, browser, referring page, timestamps, security events, and cookie or storage identifiers when the live services use them.</li>
              <li><strong>Information about another person,</strong> such as a gift recipient. Please provide it only when you have authority to do so.</li>
            </ul>
          </section>

          <section>
            <h2>3. How information is collected and held</h2>
            <p>
              Information may come directly from you; automatically from your
              browser or device; from a gift purchaser or business customer; or
              from providers supporting payment, delivery, fraud prevention,
              customer care, analytics, and communications. A production store
              may hold it in access-controlled commerce, support, payment, and
              business systems appropriate to the purpose.
            </p>
            <p>
              Where it is lawful and practical, you may browse or ask a general
              question without identifying yourself or by using a pseudonym. We
              need accurate identifying and delivery information to fulfil an
              order, process a payment or refund, or verify a privacy request.
            </p>
          </section>

          <section id="privacy-use">
            <h2>4. Why we use personal information</h2>
            <ul>
              <li>to provide products, accept and fulfil orders, take payment, deliver parcels, and manage returns or recalls;</li>
              <li>to provide support, answer inquiries, keep records, and communicate essential service or safety information;</li>
              <li>to operate, secure, troubleshoot, measure, and improve the store;</li>
              <li>to prevent fraud, misuse, chargebacks, and security incidents;</li>
              <li>to send marketing where consent or another lawful basis exists, subject to the <Link to="/marketing-policy">marketing communications policy</Link>;</li>
              <li>to comply with law, resolve disputes, and enforce agreements.</li>
            </ul>
            <p>
              If information is needed for a materially different purpose, we
              will provide any notice or seek any consent required by law.
            </p>
          </section>

          <section id="privacy-sharing">
            <h2>5. Disclosure and overseas access</h2>
            <p>
              We disclose only what is reasonably necessary to providers and
              professional advisers supporting ecommerce hosting, payment,
              delivery, communications, customer care, security, analytics,
              accounting, and legal compliance. We may also disclose information
              where authorised or required by law, to protect rights or safety,
              or as part of a genuine business transaction subject to appropriate
              safeguards.
            </p>
            <p>
              The current browser-only forms have no configured overseas
              recipient. A live store may use providers or carriers located
              outside Australia. Before that processing begins, this section
              should identify the likely countries where practicable and the
              store should take the steps required for cross-border disclosure.
              A provider's own policy applies when you use its separately
              controlled service.
            </p>
            <p>
              We do not sell personal information for money. If future analytics
              or advertising is legally treated as a sale, sharing, or targeted
              advertising, we will provide the required notice and opt-out method
              before using it.
            </p>
          </section>

          <section>
            <h2>6. Cookies and browser storage</h2>
            <p>
              This storefront uses local browser storage for cart and wishlist
              continuity and short-lived session storage for a checkout
              confirmation. It does not currently run advertising cookies. See
              the <Link to="/cookies">cookie policy</Link> for the current keys,
              purposes, and browser controls.
            </p>
          </section>

          <section>
            <h2>7. Retention and security</h2>
            <p>
              We keep personal information only as long as reasonably needed for
              the purpose collected and for legal, tax, accounting, food
              traceability, dispute, fraud-prevention, and security requirements.
              Information is then deleted, de-identified, or securely isolated as
              appropriate.
            </p>
            <p>
              We use safeguards proportionate to the information and service, but
              no internet transmission or storage system is completely secure.
              Read the <Link to="/security">security policy</Link> for reporting
              instructions and customer precautions.
            </p>
          </section>

          <section id="privacy-rights">
            <h2>8. Access, correction, deletion, and choices</h2>
            <p>
              Depending on applicable law, you may ask to access, correct, delete,
              restrict, object to, or receive a copy of personal information, and
              may withdraw consent or opt out of certain processing. We may need
              to verify identity and authority before acting. If we cannot fulfil
              a request, we will explain why where required and identify available
              review or complaint steps.
            </p>
            <p>
              You may unsubscribe from marketing at any time. Essential order,
              security, delivery, recall, and legal messages may still be sent.
              We do not discriminate against a person for exercising a privacy
              right.
            </p>
          </section>

          <section id="privacy-complaints">
            <h2>9. Privacy complaints</h2>
            <p>
              Send a complaint to <a href={`mailto:${contact.email}`}>{contact.email}</a>
              {" "}with enough detail for us to identify the issue. We will
              acknowledge it, investigate fairly, request clarification only
              where needed, and respond within a reasonable period. If you remain
              dissatisfied, you may contact the privacy or consumer regulator
              available in your jurisdiction, including the Office of the
              Australian Information Commissioner where it has authority.
            </p>
          </section>

          <section>
            <h2>10. Children</h2>
            <p>
              The store is intended for people able to make a purchase or use it
              with a parent or guardian. We do not knowingly collect personal
              information directly from a child where parental consent is
              required. Contact us if you believe a child has provided personal
              information inappropriately.
            </p>
          </section>

          <section>
            <h2>11. Changes to this policy</h2>
            <p>
              We may update this policy when practices, providers, or legal
              obligations change. The revised policy will be posted here with a
              new date, and a more prominent notice will be used where a change
              materially affects how existing information is handled.
            </p>
          </section>

          <section id="privacy-contact">
            <h2>12. Contact us</h2>
            <p>
              Privacy questions and requests can be sent through our{" "}
              <Link to="/contact">contact page</Link>
              {contact.email && (
                <>
                  {" "}or by email to{" "}
                  <a href={`mailto:${contact.email}`}>{contact.email}</a>
                </>
              )}
              .
            </p>
            {contact.address && <address>{contact.address}</address>}
            <p><Link to="/policies">View all store policies</Link>.</p>
          </section>
        </article>
      </section>
    </main>
  );
}

export default Privacy;
